import React, { useEffect, useRef } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import sr from '@utils/sr';
import { srConfig } from '@config';
import { Icon } from '@components/icons';
import { usePrefersReducedMotion } from '@hooks';

const StyledFeaturedSection = styled.section`
  max-width: 1200px;
  margin: 0 auto;
`;

const StyledProjectsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 60px;
  margin-top: 50px;
`;

const StyledProjectCard = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  background: var(--surface-color);
  border: 1px solid var(--text-dark);
  border-radius: var(--border-radius-lg);
  overflow: hidden;
  transition: var(--transition);

  @media (min-width: 900px) {
    flex-direction: row;
    min-height: 400px;
  }

  &:hover {
    border-color: var(--accent-main);
    transform: translateY(-5px);
    box-shadow: 0 20px 40px -15px rgba(0,0,0,0.8);

    .project-image .img {
      transform: scale(1.05);
      filter: grayscale(0%);
    }
  }

  .project-content {
    flex: 1;
    padding: 50px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    background: var(--surface-light);
    min-width: 0;

    @media (max-width: 768px) {
      padding: 30px;
    }

    @media (max-width: 480px) {
      padding: 20px;
    }
  }

  .project-overline {
    margin: 10px 0;
    color: var(--accent-main);
    font-family: var(--font-mono);
    font-size: var(--fz-xs);
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.1em;
  }

  .project-title {
    color: var(--text-main);
    font-size: clamp(28px, 5vw, 40px);
    font-weight: 800;
    margin: 0 0 20px;
    line-height: 1.1;

    @media (max-width: 480px) {
      font-size: clamp(24px, 8vw, 30px);
    }

    a {
      transition: var(--transition);
      &:hover {
        color: var(--accent-main);
      }
    }
  }

  .project-description {
    color: var(--text-muted);
    font-size: var(--fz-lg);
    line-height: 1.6;
    margin-bottom: 30px;

    a {
      ${({ theme }) => theme.mixins.inlineLink};
    }
  }

  .project-tech-list {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 30px;
    padding: 0;
    list-style: none;

    li {
      color: var(--text-main);
      font-family: var(--font-mono);
      font-size: var(--fz-xs);
      background: var(--bg-color);
      padding: 5px 15px;
      border-radius: 100px;
      border: 1px solid var(--text-dark);
    }
  }

  .project-links {
    display: flex;
    align-items: center;
    gap: 20px;

    a {
      display: flex;
      align-items: center;
      gap: 8px;
      color: var(--text-main);
      font-weight: 600;
      font-size: var(--fz-sm);
      transition: var(--transition);

      svg {
        width: 20px;
        height: 20px;
      }
      
      &:hover {
        color: var(--accent-main);
        transform: translateY(-2px);
      }

      &.cta {
        ${({ theme }) => theme.mixins.smallButton};
      }
    }
  }

  .project-image {
    flex: 1.5;
    position: relative;
    overflow: hidden;
    background: var(--bg-color);

    @media (max-width: 900px) {
      height: 300px;
    }

    a {
      width: 100%;
      height: 100%;
      display: block;
    }

    .img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: all 0.5s cubic-bezier(0.645, 0.045, 0.355, 1);
      filter: grayscale(80%);
    }
  }
`;

const Featured = () => {
  const data = useStaticQuery(graphql`
    {
      featured: allMarkdownRemark(
        filter: { fileAbsolutePath: { regex: "/content/featured/" } }
        sort: { fields: [frontmatter___date], order: ASC }
      ) {
        edges {
          node {
            frontmatter {
              title
              cover {
                childImageSharp {
                  gatsbyImageData(width: 800, placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
                }
              }
              tech
              github
              external
              cta
            }
            html
          }
        }
      }
    }
  `);

  const featuredProjects = data.featured.edges.filter(({ node }) => node);
  const revealTitle = useRef(null);
  const revealProjects = useRef([]);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealTitle.current, srConfig());
    revealProjects.current.forEach((ref, i) => sr.reveal(ref, srConfig(i * 100)));
  }, []);

  return (
    <StyledFeaturedSection id="projects">
      <h2 className="medium-heading" ref={revealTitle}>Featured Work</h2>

      <StyledProjectsGrid>
        {featuredProjects &&
          featuredProjects.map(({ node }, i) => {
            const { frontmatter, html } = node;
            const { external, title, tech, github, cover, cta } = frontmatter;
            const image = getImage(cover);

            return (
              <StyledProjectCard key={i} ref={el => (revealProjects.current[i] = el)}>
                <div className="project-content">
                  <p className="project-overline">Featured Project</p>
                  <h3 className="project-title">
                    <a href={external ? external : github ? github : '#'}>{title}</a>
                  </h3>

                  <div
                    className="project-description"
                    dangerouslySetInnerHTML={{ __html: html }}
                  />

                  {tech.length && (
                    <ul className="project-tech-list">
                      {tech.map((tech, i) => (
                        <li key={i}>{tech}</li>
                      ))}
                    </ul>
                  )}

                  <div className="project-links">
                    {cta && (
                      <a href={cta} aria-label="Course Link" className="cta">
                        Learn More
                      </a>
                    )}
                    {github && (
                      <a href={github} aria-label="GitHub Link" target="_blank" rel="noopener noreferrer">
                        <Icon name="GitHub" /> Code
                      </a>
                    )}
                    {external && !cta && (
                      <a href={external} aria-label="External Link" className="external" target="_blank" rel="noopener noreferrer">
                        <Icon name="External" /> Live
                      </a>
                    )}
                  </div>
                </div>

                <div className="project-image">
                  <a href={external ? external : github ? github : '#'}>
                    <GatsbyImage image={image} alt={title} className="img" />
                  </a>
                </div>
              </StyledProjectCard>
            );
          })}
      </StyledProjectsGrid>
    </StyledFeaturedSection>
  );
};

export default Featured;
